/**
 * @file CoreSecret — opaque container for plaintext credential values.
 *
 * A plain `string` field on a service is silently included in
 * `JSON.stringify`, `util.inspect`, spread expressions, template
 * strings, `new Error(\`...${pwd}...\`)` — every leak vector you'd
 * have to audit individually. `CoreSecret` wraps the value behind
 * a closure-captured private field those paths can't reach; they
 * see `'[REDACTED]'` instead. The only path back to the plaintext
 * is `expose()`, which is loud enough to flag in code review.
 *
 * Default implementation is in-memory. Subclass to customise the
 * source if a future need arises (e.g. a credential continuously
 * re-rendered by an external agent could override `expose()` to
 * read the latest copy on demand).
 *
 * @module
 */

const REDACTED = '[REDACTED]'

/**
 * Opaque wrapper around a plaintext credential value.
 *
 * @example
 * ```ts
 * class Postgres {
 *   private readonly password: CoreSecret | undefined
 *
 *   constructor(props: { password?: string }) {
 *     this.password = props.password != null
 *       ? new CoreSecret(props.password)
 *       : undefined
 *   }
 *
 *   async connect() {
 *     const pwd = this.password?.expose()  // ← only callsite that
 *                                          //   touches the plaintext
 *     // ... pass to pg.Pool, etc.
 *   }
 * }
 * ```
 */
export class CoreSecret {
  readonly #value: string

  constructor(value: string) {
    this.#value = value
  }

  /**
   * Return the plaintext value. Only call when genuinely needed —
   * each callsite is a potential leak vector and should be visible
   * in code review.
   */
  expose(): string {
    return this.#value
  }

  toJSON(): string {
    return REDACTED
  }

  toString(): string {
    return REDACTED
  }

  [Symbol.for('nodejs.util.inspect.custom')](): string {
    return REDACTED
  }
}
